// src/awsService.js
import AWS from "aws-sdk";
import JSZip from "jszip";
import { saveAs } from "file-saver";

AWS.config.update({
  accessKeyId: process.env.VUE_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.VUE_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.VUE_APP_AWS_REGION,
});

const s3 = new AWS.S3();

const listAllFiles = async (bucketName, prefix = "") => {
  const params = {
    Bucket: bucketName,
    Prefix: prefix,
  };

  let allKeys = [];
  let data;

  do {
    data = await s3.listObjectsV2(params).promise();
    allKeys = allKeys.concat(data.Contents);
    params.ContinuationToken = data.NextContinuationToken;
  } while (data.IsTruncated);

  return allKeys;
};

export const listFiles = async (bucketName, folderName = '') => {
  try {
    const prefix = folderName ? `${folderName}/` : '';
    const data = await listAllFiles(bucketName, prefix);
    return parseS3Keys(data, prefix);
  } catch (err) {
    console.error("Error listing files: ", err);
    throw err;
  }
};

export const retrieveFile = async (bucketName, key) => {
  console.log(key);
  const params = {
    Bucket: bucketName,
    Key: key,
  };
  try {
    const data = await s3.getObject(params).promise();
    return { key, body: data.Body };
  } catch (err) {
    console.error("Error retrieving file: ", err);
    throw err;
  }
};

export const getPdfFileUrl = async (bucketName, pdfKey) => {
  try {
    // if (!pdfKey.toLowerCase().endsWith(".pdf")) {
    //   throw new Error("The provided key does not point to a PDF file.");
    // }
    const params = {
      Bucket: bucketName,
      Key: pdfKey,
    };
    // Get the signed URL from S3.
    const signedUrl = s3.getSignedUrl("getObject", params);

    // Fetch the PDF file as a Blob.
    const response = await fetch(signedUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.statusText}`);
    }
    const blob = await response.blob();

    // Convert the Blob into a base64 data URL.
    const dataUrl = await blobToDataURL(blob);
    return dataUrl;
  } catch (error) {
    console.error("Error getting PDF file URL:", error);
    throw error;
  }
};

const blobToDataURL = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};


export const getFileUrl = async (bucketName, key) => {
  try {
    const params = {
      Bucket: bucketName,
      Key: key,
    };
    const url = s3.getSignedUrl('getObject', params);
    return url;
  } catch (err) {
    console.error('Error getting file URL: ', err);
    throw err;
  }
};

export const downloadFile = async (bucketName, key) => {
  try {
    const url = await getFileUrl(bucketName, key);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.responseType = "blob";

      xhr.onload = function () {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute("download", key); // Use the key as the filename
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.log(`Download of ${key} is complete.`);
          resolve();
        } else {
          console.error("Error downloading file: ", xhr.statusText);
          reject(new Error(`Error downloading file: ${xhr.statusText}`));
        }
      };

      xhr.onerror = function () {
        console.error("Network error while downloading file.");
        reject(new Error("Network error while downloading file."));
      };

      xhr.send();
    });
  } catch (err) {
    console.error("Error initiating download: ", err);
    throw err;
  }
};

// export const downloadFile = async (bucketName, key) => {
//   try {
//     const file = await retrieveFile(bucketName, key);
//     const url = window.URL.createObjectURL(new Blob([file.body]));
//     const link = document.createElement("a");
//     link.href = url;
//     link.setAttribute("download", key); // Use the key as the filename
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   } catch (err) {
//     console.error("Error downloading file: ", err);
//   }
// };


export const downloadFolder = async (bucketName, folderKey) => {
  console.log(folderKey);
  const params = {
    Bucket: bucketName,
    Prefix: folderKey.endsWith("/") ? folderKey : `${folderKey}/`,
  };

  const zip = new JSZip();

  try {
    const listData = await s3.listObjectsV2(params).promise();
    const fileKeys = listData.Contents.map((item) => item.Key);

    const filePromises = fileKeys.map(async (fileKey) => {
      const { key, body } = await retrieveFile(bucketName, fileKey);
      zip.file(key.replace(params.Prefix, ""), body);
    });

    await Promise.all(filePromises);

    const zipContent = await zip.generateAsync({ type: "blob" });
    saveAs(zipContent, `${folderKey.split("/").pop()}.zip`);
  } catch (err) {
    console.error("Error downloading folder: ", err);
    throw err;
  }
};

const parseS3Keys = (keys, prefix = '') => {
  const fileTree = {};

  keys
    .filter(({ Key }) => !Key.endsWith("/"))
    .forEach(({ Key, LastModified }) => {
      const trimmedKey = Key.startsWith(prefix) ? Key.slice(prefix.length) : Key;
      const parts = trimmedKey.split("/");
      let current = fileTree;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (i === parts.length - 1) {
          current[part] = { lastModified: LastModified };
        } else {
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
      }
    });

  const sortTree = (tree) => {
    const sortedTree = {};
    // first folders, alphabetically
    const folders = Object.keys(tree)
      .filter(key => typeof tree[key] === 'object' && !tree[key].lastModified)
      .sort();
    folders.forEach(folder => {
      sortedTree[folder] = sortTree(tree[folder]);
    });

    // then files, in custom extension order
    const extensionOrder = ['pptx', 'pdf', 'docx'];
    const files = Object.keys(tree)
      .filter(key => tree[key].lastModified)
      .sort((a, b) => {
        const extA = a.split('.').pop().toLowerCase();
        const extB = b.split('.').pop().toLowerCase();
        const idxA = extensionOrder.indexOf(extA) !== -1
          ? extensionOrder.indexOf(extA)
          : extensionOrder.length;
        const idxB = extensionOrder.indexOf(extB) !== -1
          ? extensionOrder.indexOf(extB)
          : extensionOrder.length;
        // if different groups, sort by our priority array
        if (idxA !== idxB) return idxA - idxB;
        // otherwise, fallback to alphabetic
        return a.localeCompare(b);
      });
    files.forEach((file) => {
      sortedTree[file] = tree[file];
    });

    return sortedTree;
  };

  return sortTree(fileTree);
};

export default s3;
